import { SlidersHorizontalIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { sortAgeBrackets } from 'components/hooks/quotes/useEmployeeFilters'
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuPortal, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger } from 'components/ui/dropdown-menu'
import { Button, Checkbox } from 'slab'
import { cn } from 'utils'

type Props = {
  employeeCountByState: Record<string, number>,
  employeeCountByPayType: Record<string, number>,
  employeeCountByAgeBrackets: Record<string, number>,
  filteredStates: string[],
  filteredPayTypes: string[],
  filteredAgeBrackets: string[],
  onStateFilterChange: (state: string, checked: boolean) => void,
  onPayTypeFilterChange: (payType: string, checked: boolean) => void,
  onAgeBracketFilterChange: (ageBracket: string, checked: boolean) => void,
  onClearAllFilters?: () => void
}

// Custom bounce animation for filter count
const styles = `
  @keyframes bounce-up-down {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-4px); }
  }
  .animate-bounce-up-down {
    animation: bounce-up-down infinite 0.5s ease-in-out;
  }
`

function FilterMenu ({
  employeeCountByState,
  employeeCountByPayType,
  employeeCountByAgeBrackets,
  filteredStates,
  filteredPayTypes,
  filteredAgeBrackets,
  onStateFilterChange,
  onPayTypeFilterChange,
  onAgeBracketFilterChange,
  onClearAllFilters
}: Props) {
  const [bounceFilterNumberDisplay, setBounceFilterNumberDisplay] = useState(false)
  const prevFilterCount = useRef(filteredStates.length + filteredPayTypes.length + filteredAgeBrackets.length)

  useEffect(() => {
    const currentFilterCount = filteredStates.length + filteredPayTypes.length + filteredAgeBrackets.length
    if (currentFilterCount !== prevFilterCount.current) {
      setBounceFilterNumberDisplay(true)
      const timer = setTimeout(() => setBounceFilterNumberDisplay(false), 1000)
      prevFilterCount.current = currentFilterCount
      return () => clearTimeout(timer)
    }
  }, [filteredStates.length, filteredPayTypes.length, filteredAgeBrackets.length])

  return (
    <>
      <style>{styles}</style>
      <DropdownMenu>
        <DropdownMenuTrigger asChild data-testid='filter-dropdown'>
          <Button variant='secondary' style={{ height: '2.5rem' }}>
            <div className='flex items-center gap-2 relative'>
              <SlidersHorizontalIcon className='w-4 h-4' />
              <span>Filters</span>
              {filteredStates.length + filteredPayTypes.length > 0 && (
                <span
                  style={{ fontSize: '0.6rem' }}
                  className={cn(
                    'absolute -left-2 -top-2 text-xs tabular-nums bg-red-400 text-white rounded-full px-1 h-4 w-4 flex items-center justify-center',
                    bounceFilterNumberDisplay && 'animate-bounce-up-down'
                  )}
                >
                  {filteredStates.length + filteredPayTypes.length + filteredAgeBrackets.length}
                </span>
              )}
            </div>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className='w-56' collisionPadding={50}>
          <DropdownMenuLabel>Employee filters</DropdownMenuLabel>
          <DropdownMenuGroup>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                State {filteredStates.length > 0 && <span className='bg-red-400 text-white rounded-full h-4 w-4 flex items-center justify-center' style={{ fontSize: '0.6rem' }}>{filteredStates.length}</span>}
              </DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent className='max-h-96 overflow-y-auto' collisionPadding={50}>
                  {Object.entries(employeeCountByState).sort((a, b) => a[0].localeCompare(b[0])).map(([state, count]) => (
                    <DropdownMenuItem
                      key={state}
                      onSelect={(e) => e.preventDefault()}
                      aria-label={`${state}-filter-checkbox`}
                    >
                      <Checkbox
                        id={state}
                        checked={filteredStates.includes(state)}
                        onChange={e => onStateFilterChange(state, e.target.checked)}
                      />
                      <label htmlFor={state}>
                        <span className='text-sm text-gray-800'>{state}</span>
                        <span className='text-xs text-gray-500 ml-2'>({count})</span>
                      </label>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                Age {filteredAgeBrackets.length > 0 && <span className='bg-red-400 text-white rounded-full h-4 w-4 flex items-center justify-center' style={{ fontSize: '0.6rem' }}>{filteredAgeBrackets.length}</span>}
              </DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent className='max-h-96 overflow-y-auto' collisionPadding={50}>
                  {Object.entries(employeeCountByAgeBrackets).sort(sortAgeBrackets).map(([ageBracket, count]) => (
                    <DropdownMenuItem
                      key={ageBracket}
                      onSelect={(e) => e.preventDefault()}
                      aria-label={`${ageBracket}-filter-checkbox`}
                    >
                      <Checkbox
                        id={ageBracket}
                        checked={filteredAgeBrackets.includes(ageBracket)}
                        onChange={e => onAgeBracketFilterChange(ageBracket, e.target.checked)}
                      />
                      <label htmlFor={ageBracket}>
                        <span className='text-sm text-gray-800'>{ageBracket}</span>
                        <span className='text-xs text-gray-500 ml-2'>({count})</span>
                      </label>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>
            {Object.keys(employeeCountByPayType).length > 1 && (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  Pay type {filteredPayTypes.length > 0 && <span className='bg-red-400 text-white rounded-full h-4 w-4 flex items-center justify-center' style={{ fontSize: '0.6rem' }}>{filteredPayTypes.length}</span>}
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent className='max-h-96 overflow-y-auto' collisionPadding={50}>
                    {Object.entries(employeeCountByPayType).map(([payType, count]) => (
                      <DropdownMenuItem key={payType} onSelect={(e) => e.preventDefault()}>
                        <Checkbox
                          id={payType}
                          checked={filteredPayTypes.includes(payType)}
                          onChange={e => onPayTypeFilterChange(payType, e.target.checked)}
                        />
                        <label htmlFor={payType}>
                          <span className='text-sm text-gray-80 capitalize'>{payType}</span>
                          <span className='text-xs text-gray-500 ml-2'>({count})</span>
                        </label>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
            )}
          </DropdownMenuGroup>
          {(filteredStates.length > 0 || filteredPayTypes.length > 0 || filteredAgeBrackets.length > 0) && onClearAllFilters && (
            <div className='border-t border-gray-200 pt-2 mt-2'>
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault()
                  onClearAllFilters()
                }}
                className='text-red-600 hover:text-red-700 hover:bg-red-50 cursor-pointer'
              >
                Clear all filters
              </DropdownMenuItem>
            </div>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  )
}

export default FilterMenu
